import { LOG_TYPES } from '../constants/logTypeRegistry';
import { normalizeBackendCaptureReviewRows } from './naturalCaptureReviewNormalization';
import { isQuickLogLongNoteText } from './quickLogRoutingDecision';
import { groupRowsForReview } from './reviewRowGrouping';
import {
  applyImportDateContextToImportedRows,
  isLongNoteEmailText,
  parseNaturalCaptureText,
} from '../components/Dashboard/ImportLogs/importLogsParsing';
import {
  buildNaturalCapturePipelineSnapshot,
  detectNaturalCaptureInputType,
  traceNaturalCapturePipeline,
} from '../components/Dashboard/ImportLogs/importLogsPipeline';

export const normalizeBackendQuickLogEntries = (entries = [], rawText = '') => (
  normalizeBackendCaptureReviewRows(entries, {
    parserMode: 'backend_quick_log',
    sourceMode: 'quick_log_backend',
    inputMode: 'quick_log',
    rawText,
    backendSource: 'claude',
    parsedBy: 'claude',
  }).map((entry) => ({
    ...entry,
    titlePrefix: entry?.titlePrefix || LOG_TYPES[entry?.category]?.displayLabel || (entry?.category === 'log' ? 'Note' : entry?.category),
    source: entry?.source || entry?.metadata?.backendSource || entry?.metadata?.parsedBy || 'claude',
  }))
);

export const summarizeBackendQuickLogResponse = (responseData = {}, backendEntries = [], error = null) => {
  const responseKeys = responseData && typeof responseData === 'object' ? Object.keys(responseData) : [];
  const entryPreview = Array.isArray(backendEntries)
      ? backendEntries.slice(0, 6).map((entry) => ({
        cat: entry?.cat || entry?.category || null,
        title: entry?.title || entry?.note || null,
        note: entry?.note || entry?.text || null,
        time: entry?.time || null,
        timestamp: entry?.timestamp || entry?.metadata?.inferredTimestamp || null,
        timeSource: entry?.timeSource || null,
        timeBucket: entry?.metadata?.timeBucket || null,
        timeInferred: Boolean(entry?.timeInferred || entry?.metadata?.timeInferred),
        timeInferenceReason: entry?.timeInferenceReason || entry?.metadata?.timeInferenceReason || null,
        capturedAt: entry?.metadata?.capturedAt || null,
        confidence: entry?.confidence || null,
      }))
    : [];

  return {
    ok: Boolean(responseData?.ok),
    source: responseData?.source || responseData?.extractedBy || null,
    reviewNeeded: Boolean(responseData?.reviewNeeded),
    entryCount: Array.isArray(backendEntries) ? backendEntries.length : 0,
    responseKeys,
    entryPreview,
    error: error ? {
      message: error?.message || String(error),
      code: error?.code || null,
    } : null,
  };
};

export const extractBackendQuickLogEntries = (responseData = {}) => {
  const candidateArrays = [
    responseData?.entries,
    responseData?.result?.entries,
    responseData?.data?.entries,
    responseData?.result?.data?.entries,
  ];

  for (const candidate of candidateArrays) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  return [];
};

export const resolveQuickLogLongNoteRows = ({
  text = '',
  child = null,
  defaultDate = new Date(),
} = {}) => {
  const rawText = String(text || '').trim();
  if (!rawText || !isQuickLogLongNoteText(rawText)) {
    return null;
  }

  traceNaturalCapturePipeline('quick-log-long-note-input', buildNaturalCapturePipelineSnapshot({
    stage: 'quick-log-long-note-input',
    inputType: detectNaturalCaptureInputType({
      source: 'quick_log',
      isLongNote: true,
    }),
    text: rawText,
  }));

  const parsedLocalRows = parseNaturalCaptureText(rawText, {
    defaultDate,
    childName: child?.name || null,
    adapterMode: 'long_note_email',
    parserMode: 'long_note_email',
    sourceMode: 'long_note_email',
    inputMode: 'import',
    rawText,
  });
  const localRows = Array.isArray(parsedLocalRows?.rows) ? parsedLocalRows.rows : [];
  const datedLocalRows = applyImportDateContextToImportedRows(localRows, rawText, {
    fallbackDate: defaultDate,
  });
  const groupedRows = groupRowsForReview(datedLocalRows);

  traceNaturalCapturePipeline('quick-log-long-note-split', buildNaturalCapturePipelineSnapshot({
    stage: 'quick-log-long-note-split',
    inputType: detectNaturalCaptureInputType({
      source: 'quick_log',
      isLongNote: true,
    }),
    text: rawText,
    rows: localRows,
    groupedRows,
  }));

  return {
    parserMode: isLongNoteEmailText(rawText) ? 'long_note_email' : 'long_note_narrative',
    longNoteDetected: true,
    localParsedRowCount: localRows.length,
    groupedRowCount: groupedRows.length,
    rows: groupedRows,
  };
};
